// SPDX-License-Identifier: AGPL-3.0-or-later
import {Logger} from '@app/features/platform/utils/AppLogger';

const logger = new Logger('TtsWasmFallback');
type MeSpeak = {
	loadConfig: (config: unknown) => void;
	loadVoice: (voice: unknown) => boolean;
	speak: (
		text: string,
		opts: {
			rawdata: string;
			speed?: number;
		},
	) => ArrayBuffer | undefined;
};
let initState: 'unloaded' | 'loading' | 'ready' | 'failed' = 'unloaded';
let initPromise: Promise<MeSpeak> | null = null;
let meSpeakInstance: MeSpeak | null = null;
let activeSource: AudioBufferSourceNode | null = null;
let activeContext: AudioContext | null = null;
async function init(): Promise<MeSpeak> {
	if (meSpeakInstance) return meSpeakInstance;
	if (initPromise) return initPromise;
	initState = 'loading';
	initPromise = (async () => {
		const [meSpeakModule, configModule, voiceModule] = await Promise.all([
			import('mespeak'),
			import('mespeak/src/mespeak_config.json'),
			import('mespeak/voices/en/en-us.json'),
		]);
		const meSpeak = (meSpeakModule.default ?? meSpeakModule) as MeSpeak;
		meSpeak.loadConfig(configModule.default ?? configModule);
		meSpeak.loadVoice(voiceModule.default ?? voiceModule);
		meSpeakInstance = meSpeak;
		initState = 'ready';
		return meSpeak;
	})();
	try {
		return await initPromise;
	} catch (err) {
		initState = 'failed';
		initPromise = null;
		throw err;
	}
}
export interface WasmSpeakOptions {
	text: string;
	rate?: number;
	onStart?: () => void;
	onEnd?: () => void;
	onError?: (error: string) => void;
}
function stopActive(): void {
	if (activeSource) {
		try {
			activeSource.onended = null;
			activeSource.stop();
		} catch {}
		activeSource = null;
	}
	if (activeContext) {
		activeContext.close().catch(() => {});
		activeContext = null;
	}
}
export async function speak(options: WasmSpeakOptions): Promise<void> {
	let meSpeak: MeSpeak;
	try {
		meSpeak = await init();
	} catch (err) {
		logger.warn('mespeak init failed', err);
		options.onError?.('init-failed');
		options.onEnd?.();
		return;
	}
	stopActive();
	const wpm = Math.max(80, Math.min(450, Math.round(175 * (options.rate ?? 1))));
	let wav: ArrayBuffer | undefined;
	try {
		wav = meSpeak.speak(options.text, {rawdata: 'arraybuffer', speed: wpm}) ?? undefined;
	} catch (err) {
		logger.warn('mespeak.speak threw', err);
		options.onError?.('synthesis-failed');
		options.onEnd?.();
		return;
	}
	if (!(wav instanceof ArrayBuffer)) {
		options.onError?.('synthesis-empty');
		options.onEnd?.();
		return;
	}
	const audioCtx = new AudioContext();
	activeContext = audioCtx;
	let buffer: AudioBuffer;
	try {
		buffer = await audioCtx.decodeAudioData(wav.slice(0));
	} catch (err) {
		logger.warn('decodeAudioData failed', err);
		if (activeContext === audioCtx) {
			activeContext = null;
		}
		audioCtx.close().catch(() => {});
		options.onError?.('decode-failed');
		options.onEnd?.();
		return;
	}
	if (activeContext !== audioCtx) {
		audioCtx.close().catch(() => {});
		options.onEnd?.();
		return;
	}
	const source = audioCtx.createBufferSource();
	source.buffer = buffer;
	source.connect(audioCtx.destination);
	source.onended = () => {
		if (activeSource === source) {
			activeSource = null;
		}
		if (activeContext === audioCtx) {
			activeContext = null;
		}
		audioCtx.close().catch(() => {});
		options.onEnd?.();
	};
	activeSource = source;
	source.start();
	options.onStart?.();
}
export function cancel(): void {
	stopActive();
}
export function isReady(): boolean {
	return initState === 'ready';
}
export function hasFailed(): boolean {
	return initState === 'failed';
}
export function preload(): Promise<void> {
	return init().then(
		() => undefined,
		() => undefined,
	);
}
